import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { computeSortOrder, applyPerm, remapRowKeys, remapMerges } from "../utils/sortUtils";

const EMPTY_OBJ = {};
const EMPTY_LEVELS = [];

/**
 * useSort
 * Owns sorting for the active sheet and produces the *display* versions
 * of the row data and every per-row keyed map (heights, styles, rich
 * HTML, links, merges, formulas).  When no sort is active it returns the
 * raw inputs unchanged (same identities → zero render churn).
 *
 * Sort is MULTI-LEVEL (Excel "Custom Sort"): `levels` is an ordered list
 * of { col, dir }.  A quick single-column sort is just a one-entry list.
 *
 * Behaviour mirrors freeze / hide-unhide:
 *   - state is checkpointed per sheet in a ref map and restored on tab
 *     switch;
 *   - on first visit to a sheet the file's saved sortState is applied;
 *   - the "first row is header" assumption is optional and persisted too.
 */
export default function useSort({
  activeSheet, prevSheetRef, activeModel,
  rawRows, rawHeights, rawStyles, rawRich, rawLinks, rawMerges,
}) {
  const sortBySheetRef = useRef({});
  const [levels, setLevels] = useState(EMPTY_LEVELS);     // [{ col, dir }]
  const [hasHeaderRow, setHasHeaderRowState] = useState(true);

  // Save the outgoing sheet's sort, then restore (or initialize from the
  // file) the incoming sheet's sort.
  useEffect(() => {
    const prev = prevSheetRef?.current;
    if (prev && prev !== activeSheet) {
      sortBySheetRef.current[prev] = { levels, hasHeaderRow };
    }
    const saved = sortBySheetRef.current[activeSheet];
    if (saved) {
      setLevels(saved.levels || EMPTY_LEVELS);
      setHasHeaderRowState(saved.hasHeaderRow !== false);
    } else {
      const fileSort = activeModel?.sortState;
      setLevels(
        fileSort && fileSort.col != null
          ? [{ col: fileSort.col, dir: fileSort.descending ? "desc" : "asc" }]
          : EMPTY_LEVELS,
      );
      setHasHeaderRowState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheet]);

  // Heavy step — keyed on the ROWS identity + dataRowCount (both stable
  // across a width/height resize) so resizing never triggers a re-sort.
  const order = useMemo(() => {
    if (!activeModel || levels.length === 0) return null;
    return computeSortOrder(activeModel.rows, {
      levels,
      headerRows: hasHeaderRow ? 1 : 0,
      dataRowCount: activeModel.dataRowCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModel?.rows, activeModel?.dataRowCount, levels, hasHeaderRow]);

  // Cheap per-field remaps — each re-runs only when its own source changes.
  const normalizedRows = useMemo(
    () => (order ? applyPerm(activeModel.rows, order.perm) : rawRows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, activeModel?.rows, rawRows],
  );
  const rowHeights = useMemo(
    () => (order ? applyPerm(rawHeights, order.perm) : rawHeights),
    [order, rawHeights],
  );
  const cellStyles = useMemo(
    () => (order ? remapRowKeys(rawStyles, order.invPerm) : rawStyles),
    [order, rawStyles],
  );
  const cellRichHtml = useMemo(
    () => (order ? remapRowKeys(rawRich, order.invPerm) : rawRich),
    [order, rawRich],
  );
  const cellLinks = useMemo(
    () => (order ? remapRowKeys(rawLinks, order.invPerm) : rawLinks),
    [order, rawLinks],
  );
  const cellMerges = useMemo(
    () => (order ? remapMerges(rawMerges, order.invPerm, order.start, order.end) : rawMerges),
    [order, rawMerges],
  );
  const formulas = useMemo(
    () => (order ? remapRowKeys(activeModel?.formulas || EMPTY_OBJ, order.invPerm) : (activeModel?.formulas || EMPTY_OBJ)),
    [order, activeModel?.formulas],
  );
  // Number formats follow their cells when rows are reordered.
  const numFmts = useMemo(
    () => (order ? remapRowKeys(activeModel?.numFmts || EMPTY_OBJ, order.invPerm) : (activeModel?.numFmts || EMPTY_OBJ)),
    [order, activeModel?.numFmts],
  );
  const writeCheckpoint = useCallback((patch) => {
    if (!activeSheet) return;
    const cur = sortBySheetRef.current[activeSheet] || { levels: EMPTY_LEVELS, hasHeaderRow: true };
    sortBySheetRef.current[activeSheet] = { ...cur, ...patch };
  }, [activeSheet]);

  // Commit a new levels array (and persist it for this sheet).
  const applyLevels = useCallback((next) => {
    setLevels(next);
    writeCheckpoint({ levels: next });
  }, [writeCheckpoint]);

  const sortAsc = useCallback((c) => applyLevels([{ col: c, dir: "asc" }]), [applyLevels]);
  const sortDesc = useCallback((c) => applyLevels([{ col: c, dir: "desc" }]), [applyLevels]);
  const clearSort = useCallback(() => applyLevels(EMPTY_LEVELS), [applyLevels]);
  const setSortLevels = useCallback((lv) => applyLevels(Array.isArray(lv) ? lv : EMPTY_LEVELS), [applyLevels]);
  const applySort = useCallback((lv, header) => {
    const next = Array.isArray(lv) ? lv : EMPTY_LEVELS;
    const hdr = header !== false;
    setLevels(next);
    setHasHeaderRowState(hdr);
    writeCheckpoint({ levels: next, hasHeaderRow: hdr });
  }, [writeCheckpoint]);

  // Quick cycle for a header click: none -> asc -> desc -> none (single col).
  const toggleSort = useCallback((c) => {
    setLevels((prev) => {
      let next;
      if (prev.length === 1 && prev[0].col === c) {
        next = prev[0].dir === "asc" ? [{ col: c, dir: "desc" }] : EMPTY_LEVELS;
      } else {
        next = [{ col: c, dir: "asc" }];
      }
      writeCheckpoint({ levels: next });
      return next;
    });
  }, [writeCheckpoint]);

  const setHasHeaderRow = useCallback((v) => {
    setHasHeaderRowState(v);
    writeCheckpoint({ hasHeaderRow: v });
  }, [writeCheckpoint]);

  // Helper for the header indicator: this column's direction (or null)
  // and its 1-based rank when more than one level is active.
  const getSortState = useCallback((c) => {
    const idx = levels.findIndex((l) => l.col === c);
    if (idx === -1) return null;
    return { dir: levels[idx].dir, rank: levels.length > 1 ? idx + 1 : 0 };
  }, [levels]);

  return {
    sortLevels: levels, hasHeaderRow, setHasHeaderRow,
    sortAsc, sortDesc, clearSort, toggleSort, setSortLevels, applySort, getSortState,
    normalizedRows, rowHeights, cellStyles, cellRichHtml, cellLinks, cellMerges, formulas, numFmts,
  };
}
